// Paging Groups — dedicated admin page wrapping the ring group "paging"
// strategy. Filters the shared ring group list to strategy=paging and
// presents a simplified, paging-focused create/edit UX. No new backend
// endpoints — all CRUD goes through the existing ring group API.

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Search, MoreVertical, Volume2, Info } from 'lucide-react';
import { api, getToken } from '../lib/api';
import { useToast } from '../components/Toast';
import { matchesScope } from '../lib/tenantScope';
import { getScopedDomain } from '../lib/tenantScope';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

type PagingGroup = {
  id: string;
  name: string;
  extension: string;
  memberCount: number;
  multicastAddress: string;
  multicastPort: number;
  domain_uuid: string;
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      {children}
    </div>
  );
}

// Modal for creating a new paging group. Pre-fills strategy=paging.
function NewPagingGroupModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const me = api.me();
  const toast = useToast();
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  const [form, setForm] = useState({
    ring_group_name: '',
    ring_group_extension: '',
    domain_uuid: me?.domain_uuid || '',
    multicast_address: '224.0.1.116',
    multicast_port: 34567,
    multicast_codec: 'PCMU',
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  // Auto-suggest next available ring group extension number
  const nextExt = useQuery<any>({
    queryKey: ['first-avail-ring-group'],
    queryFn: () => fJson('/api/functions/GetFirstAvailableRingGroupNumber'),
  });
  // Only prefill if the field is still empty
  if (nextExt.data?.number && !form.ring_group_extension) {
    setForm(f => f.ring_group_extension ? f : { ...f, ring_group_extension: String(nextExt.data.number) });
  }

  const create = useMutation({
    mutationFn: async () => {
      // Step 1: create the ring group with paging strategy
      const r: any = await fJson(`/api/tenants/${form.domain_uuid}/ring-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ring_group_name: form.ring_group_name,
          ring_group_extension: form.ring_group_extension,
          ring_group_strategy: 'paging',
          ring_group_timeout: 30,
          assigned_dids: [],
          members: [],
        }),
      });
      // Step 2: persist paging-specific settings (multicast config + strategy lock)
      if (r?.ring_group_uuid) {
        try {
          await fJson(`/api/call-routing/ring-groups/${r.ring_group_uuid}/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              strategy: 'paging',
              ring_time_seconds: 30,
              multicast_address: form.multicast_address,
              multicast_port: form.multicast_port,
              multicast_codec: form.multicast_codec,
              multicast_packet_time_ms: 20,
              forward_no_answer: { type: 'end_call' },
              out_of_office_route: { type: 'accept_anyway' },
              break_route: { type: 'accept_anyway' },
              holidays_route: { type: 'accept_anyway' },
            }),
          });
        } catch {
          // Non-fatal: RG exists and is editable; paging settings can be tuned in the editor
        }
      }
      return r;
    },
    onSuccess: () => {
      toast('success', `Paging group created`);
      onCreated();
      onClose();
    },
    onError: (e: any) => toast('error', String(e?.message || e)),
  });

  const canSave = form.ring_group_name && form.ring_group_extension && form.domain_uuid;

  return createPortal(
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg card p-5 space-y-4 shadow-xl">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Volume2 size={18} className="text-primary" />
          New Paging Group
        </h2>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Group Name *">
            <input
              className="input"
              value={form.ring_group_name}
              onChange={e => set('ring_group_name', e.target.value)}
              placeholder="All Phones"
              autoFocus
            />
          </Field>
          <Field label="Extension *">
            <input
              className="input font-mono"
              value={form.ring_group_extension}
              onChange={e => set('ring_group_extension', e.target.value)}
              placeholder="8100"
            />
          </Field>
          <Field label="Department *">
            <select className="input" value={form.domain_uuid} onChange={e => set('domain_uuid', e.target.value)}>
              <option value="">— Choose —</option>
              {(tenants.data || []).map((t: any) => (
                <option key={t.domain_uuid} value={t.domain_uuid}>
                  {t.domain_description || t.domain_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Multicast Address">
            <input
              className="input font-mono"
              value={form.multicast_address}
              onChange={e => set('multicast_address', e.target.value)}
              placeholder="224.0.1.116"
            />
          </Field>
          <Field label="Multicast Port">
            <input
              className="input"
              type="number"
              value={form.multicast_port}
              onChange={e => set('multicast_port', parseInt(e.target.value, 10) || 34567)}
            />
          </Field>
          <Field label="Codec">
            <select className="input" value={form.multicast_codec} onChange={e => set('multicast_codec', e.target.value)}>
              {['PCMU', 'PCMA', 'G722'].map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <p className="text-xs text-textSecondary">
          Members can be added after saving. Go to Call Handling and edit this ring group to add phones.
        </p>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary inline-flex items-center gap-1 disabled:opacity-50"
            disabled={!canSave || create.isPending}
            onClick={() => create.mutate()}
          >
            <Plus size={14} /> {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

export default function PagingGroups() {
  const me = api.me();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  // Fetch all ring groups and filter to strategy=paging
  const ringGroupsQ = useQuery<any[]>({
    queryKey: ['ring-groups', me?.domain_uuid || 'all'],
    queryFn: async () => {
      const r = await fetch('/api/ring-groups', auth());
      if (!r.ok) return [];
      return r.json();
    },
  });

  // Fetch settings for each ring group to get strategy + multicast fields.
  // We batch individual settings queries so they cache alongside the editor.
  const allGroups = ringGroupsQ.data || [];
  const settingsQueries = useQuery<Record<string, any>>({
    queryKey: ['paging-group-settings-bulk', allGroups.map((g: any) => g.ring_group_uuid || g.id).join(',')],
    queryFn: async () => {
      if (!allGroups.length) return {};
      const results = await Promise.allSettled(
        allGroups.map((g: any) => {
          const id = g.ring_group_uuid || g.id;
          return fJson(`/api/call-routing/ring-groups/${id}/settings`).then((s: any) => ({ id, s }));
        })
      );
      const map: Record<string, any> = {};
      for (const r of results) {
        if (r.status === 'fulfilled') map[r.value.id] = r.value.s;
      }
      return map;
    },
    enabled: allGroups.length > 0,
  });

  const settingsMap = settingsQueries.data || {};

  // Build the paging group list: only groups where strategy=paging
  const pagingGroups: PagingGroup[] = allGroups
    .filter((g: any) => {
      const id = g.ring_group_uuid || g.id;
      const s = settingsMap[id];
      // Accept strategy from settings (authoritative) or the group's own ring_group_strategy
      const strategy = s?.strategy || g.ring_group_strategy || g.strategy;
      if (strategy !== 'paging') return false;
      if (!matchesScope(g.domain_uuid)) return false;
      return true;
    })
    .map((g: any) => {
      const id = g.ring_group_uuid || g.id;
      const s = settingsMap[id] || {};
      const memberCount = Array.isArray(s.members) ? s.members.length : (g.member_count ?? 0);
      return {
        id,
        name: g.ring_group_name || g.name || '—',
        extension: g.ring_group_extension || g.extension || '',
        memberCount,
        multicastAddress: s.multicast_address || g.multicast_address || '',
        multicastPort: s.multicast_port || g.multicast_port || 0,
        domain_uuid: g.domain_uuid || me?.domain_uuid || '',
      };
    });

  const visible = search
    ? pagingGroups.filter(pg =>
        [pg.name, pg.extension, pg.multicastAddress].join(' ').toLowerCase().includes(search.toLowerCase())
      )
    : pagingGroups;

  const remove = useMutation({
    mutationFn: (pg: PagingGroup) =>
      fetch(`/api/ring-groups/${pg.id}`, { method: 'DELETE', ...auth() }).then(async r => {
        if (!r.ok) throw new Error((await r.text().catch(() => '')) || 'Delete failed');
      }),
    onSuccess: (_r, pg) => {
      toast('success', `Paging group "${pg.name}" deleted`);
      qc.invalidateQueries({ queryKey: ['ring-groups', me?.domain_uuid || 'all'] });
      qc.invalidateQueries({ queryKey: ['ring-groups'] });
    },
    onError: (e: any) => toast('error', String(e?.message || e)),
  });

  function closeMenu() { setMenuFor(null); setMenuPos(null); }
  const menuRow = visible.find(pg => pg.id === menuFor) || null;

  const isLoading = ringGroupsQ.isLoading || settingsQueries.isLoading;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Volume2 size={22} className="text-primary" />
          Paging Groups
        </h1>
      </div>

      {/* Explainer banner */}
      <div className="card p-3 flex items-start gap-2 bg-elevated/40 text-sm text-textSecondary">
        <Info size={15} className="mt-0.5 shrink-0 text-primary" />
        <span>
          Paging groups use multicast UDP to simultaneously ring all members' phones. Dial the group extension to page.
          Phones must support multicast paging (most Grandstream, Yealink, and Polycom models do).
          Groups created here appear under Call Handling as ring groups with the Paging strategy.
        </span>
      </div>

      {/* Toolbar */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button
          className="btn-primary text-sm inline-flex items-center gap-1"
          onClick={() => setShowNew(true)}
        >
          <Plus size={14} /> New Paging Group
        </button>

        <div className="flex-1" />

        <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1">
          <Search size={14} className="text-textSecondary" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search…"
            className="bg-transparent outline-none text-sm w-40"
          />
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Extension</th>
              <th>Members</th>
              <th>Multicast Address</th>
              <th>Port</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-textSecondary">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && visible.map(pg => (
              <tr
                key={pg.id}
                className="hover:bg-elevated cursor-pointer"
                onClick={() => navigate(`/ring-groups/${pg.id}/edit`)}
              >
                <td>
                  <span className="inline-flex items-center gap-2">
                    <Volume2 size={13} className="text-primary shrink-0" />
                    {pg.name}
                  </span>
                </td>
                <td className="font-mono">{pg.extension || '—'}</td>
                <td>{pg.memberCount || 0}</td>
                <td className="font-mono text-sm">{pg.multicastAddress || '—'}</td>
                <td className="text-sm">{pg.multicastPort || '—'}</td>
                <td onClick={e => e.stopPropagation()}>
                  <button
                    className="btn-ghost p-1"
                    title="Actions"
                    onClick={e => {
                      if (menuFor === pg.id) { closeMenu(); return; }
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      const MENU_H = 80, MENU_W = 160;
                      const room_below = window.innerHeight - rect.bottom;
                      const top = room_below >= MENU_H + 8 ? rect.bottom + 4 : Math.max(8, rect.top - MENU_H - 4);
                      const left = Math.min(rect.right - MENU_W, window.innerWidth - MENU_W - 8);
                      setMenuPos({ top, left });
                      setMenuFor(pg.id);
                    }}
                  >
                    <MoreVertical size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {!isLoading && visible.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-10 text-textSecondary">
                  {pagingGroups.length === 0
                    ? 'No paging groups yet. Click "New Paging Group" to create one.'
                    : 'No matches.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Row actions menu — portal to body to escape glass card containing-block */}
      {menuFor && menuRow && createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={closeMenu} />
          <div
            className="card z-50 min-w-[160px] py-1 shadow-lg"
            style={{ position: 'fixed', top: menuPos?.top ?? 0, left: menuPos?.left ?? 0 }}
          >
            <button
              className="w-full text-left px-3 py-2 hover:bg-elevated text-sm"
              onClick={() => { const r = menuRow; closeMenu(); navigate(`/ring-groups/${r.id}/edit`); }}
            >
              Edit
            </button>
            <button
              className="w-full text-left px-3 py-2 hover:bg-danger/10 text-danger text-sm flex items-center gap-2"
              onClick={() => {
                const r = menuRow;
                closeMenu();
                if (confirm(`Delete paging group "${r.name}"?`)) remove.mutate(r);
              }}
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </>,
        document.body
      )}

      {showNew && (
        <NewPagingGroupModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['ring-groups', me?.domain_uuid || 'all'] });
            qc.invalidateQueries({ queryKey: ['ring-groups'] });
          }}
        />
      )}
    </div>
  );
}
